// Étape 13 — Page client de paiement après signature (carte / virement, échéancier, factures)
import { useState, useEffect } from "react";
import { fmtEur } from "./shared";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/public/paiement`;
const K = { navy: "#1B2A4A", gold: "#C8902C", teal: "#1F7A6C", cream: "#F7F3EA", red: "#B3261E", mid: "#5d6475", border: "#e3dccb", green: "#2E7D4F" };
const eur = fmtEur;
const fdate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
const card = { background: "#fff", border: `1px solid ${K.border}`, borderRadius: 14, padding: 18, marginBottom: 14 };
const btn = (bg, dis) => ({ background: dis ? "#c9c3b5" : bg, color: "#fff", border: "none", borderRadius: 10, padding: "13px 18px", fontWeight: 700, fontSize: 15, cursor: dis ? "not-allowed" : "pointer", width: "100%", fontFamily: "inherit" });
const titre = { fontWeight: 700, marginBottom: 10, fontFamily: "'Playfair Display', serif", fontSize: 17 };
const STAT = { a_payer: ["À régler", K.gold], virement_annonce: ["Virement attendu", K.teal], payee: ["Réglée", K.green], en_attente_jalon: ["À venir", K.mid] };

const Echeancier = ({ d }) => (
  <div style={card} data-testid="pay-echeancier">
    <div style={titre}>Votre échéancier</div>
    {d.echeances.map(e => {
      const futur = e.statut === "a_payer" && e.exigible_at && new Date(e.exigible_at) > new Date();
      const [lbl, col] = futur ? STAT.en_attente_jalon : STAT[e.statut] || [e.statut, K.mid];
      return (
        <div key={e.index} data-testid={`pay-echeance-${e.index}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${K.border}`, fontSize: 13 }}>
          <div>
            <div style={{ fontWeight: 600 }}>{e.libelle}</div>
            <div style={{ fontSize: 11, color: K.mid }}>
              {e.statut === "payee" ? `Réglée le ${fdate(e.payee_at)}${e.facture ? ` · facture ${e.facture}` : ""}` : e.statut === "en_attente_jalon" ? "Exigible à la validation de cette étape par votre consultant" : e.exigible_at ? `Exigible à partir du ${fdate(e.exigible_at)}` : ""}
            </div>
          </div>
          <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700 }}>{eur(e.montant)}</div><span style={{ fontSize: 11, fontWeight: 700, color: col }}>{lbl}</span></div>
        </div>
      );
    })}
    {d.mensuel > 0 && <div style={{ fontSize: 12, color: K.mid, marginTop: 8 }}>Abonnement : {eur(d.mensuel)} / mois, prélevé par carte à partir du premier règlement.</div>}
  </div>
);

const Rib = ({ d, montant }) => (
  <div data-testid="pay-rib" style={{ background: K.cream, borderRadius: 10, padding: 14, fontSize: 13, lineHeight: 1.7, marginTop: 10 }}>
    <div>Titulaire : <strong>{d.rib.titulaire}</strong></div>
    <div>IBAN : <strong data-testid="pay-iban" style={{ fontFamily: "monospace" }}>{d.rib.iban}</strong></div>
    <div>BIC : <strong style={{ fontFamily: "monospace" }}>{d.rib.bic}</strong></div>
    <div>Montant : <strong>{eur(montant)}</strong></div>
    <div>Référence à indiquer impérativement : <strong data-testid="pay-ref">{d.ref_virement}</strong></div>
  </div>
);

const Regler = ({ token, d, reload }) => {
  const [choix, setChoix] = useState(d.due?.statut === "virement_annonce" ? "virement" : "carte");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const e = d.due;
  if (!e) return null;
  if (!d.exigible) return (
    <div style={{ ...card, borderColor: K.gold }} data-testid="pay-attente-j7">
      Conformément à la loi, le paiement sera possible à partir du <strong>{fdate(e.exigible_at)}</strong> (7e jour suivant votre signature). Votre lien de paiement vous sera envoyé par e-mail à cette date.
    </div>
  );
  const carte = async () => {
    setBusy(true); setErr("");
    try { const r = await axios.post(`${API}/${token}/carte`, { origin_url: window.location.origin }); window.location.href = r.data.checkout_url; }
    catch (x) { setErr(x?.response?.data?.detail || "Erreur"); setBusy(false); }
  };
  const virement = async () => {
    setBusy(true); setErr("");
    try { await axios.post(`${API}/${token}/virement`); await reload(); } catch (x) { setErr(x?.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  const opt = (k, l, sub) => (
    <label style={{ flex: 1, display: "flex", gap: 10, padding: 12, borderRadius: 10, cursor: "pointer", border: `1.5px solid ${choix === k ? K.navy : K.border}`, background: choix === k ? "#f3f0e8" : "#fff" }}>
      <input type="radio" checked={choix === k} onChange={() => setChoix(k)} data-testid={`pay-choix-${k}`} />
      <div><div style={{ fontWeight: 700, fontSize: 14 }}>{l}</div><div style={{ fontSize: 11, color: K.mid }}>{sub}</div></div>
    </label>
  );
  return (
    <div style={{ ...card, borderColor: K.gold }} data-testid="pay-regler">
      <div style={titre}>Régler : {e.libelle} — {eur(e.montant + (e.index === 1 && d.mensuel ? d.mensuel : 0))}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {opt("carte", "Carte bancaire", "Paiement sécurisé immédiat")}
        {opt("virement", "Virement bancaire", "Facture émise à réception")}
      </div>
      {choix === "carte" && <button style={btn(K.gold, busy)} disabled={busy} onClick={carte} data-testid="pay-carte-btn">{busy ? "Redirection…" : `Payer ${eur(e.montant + (e.index === 1 && d.mensuel ? d.mensuel : 0))} par carte`}</button>}
      {choix === "virement" && (<>
        <Rib d={d} montant={e.montant} />
        {e.statut === "virement_annonce"
          ? <div data-testid="pay-virement-ok" style={{ fontSize: 13, color: K.teal, marginTop: 10 }}>✓ Merci, votre virement est attendu. Votre facture acquittée vous sera envoyée dès sa réception.</div>
          : <button style={{ ...btn(K.navy, busy), marginTop: 10 }} disabled={busy} onClick={virement} data-testid="pay-virement-btn">Je règle par virement</button>}
      </>)}
      {err && <div style={{ color: K.red, fontSize: 13, marginTop: 10 }} data-testid="pay-erreur">{err}</div>}
    </div>
  );
};

const Factures = ({ d }) => d.factures.length > 0 && (
  <div style={card} data-testid="pay-factures">
    <div style={titre}>Vos factures</div>
    {d.factures.map(f => (
      <a key={f.numero} href={`${BACKEND}${f.link}`} target="_blank" rel="noreferrer" data-testid={`pay-facture-${f.numero}`}
        style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "6px 0", color: K.teal, fontWeight: 600, textDecoration: "none" }}>
        <span>⬇ {f.type === "avoir" ? "Avoir" : "Facture"} {f.numero}</span><span>{eur(f.montant_ttc)}</span>
      </a>
    ))}
  </div>
);

const useRetourStripe = (reload) => {
  const sid = new URLSearchParams(window.location.search).get("session_id");
  const [etat, setEtat] = useState(sid ? "verif" : null);
  useEffect(() => {
    if (!sid) return;
    let n = 0, stop = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") { setEtat("ok"); reload(); return; }
      } catch { /* on réessaie */ }
      if (++n < 10 && !stop) setTimeout(tick, 2000); else setEtat("attente");
    };
    tick();
    return () => { stop = true; };
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  return etat;
};

export default function PaiementPage({ token, fallback }) {
  const [d, setD] = useState(undefined);
  const load = () => axios.get(`${API}/${token}`).then(r => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const retour = useRetourStripe(load);
  if (d === undefined) return <div style={{ padding: 40, textAlign: "center", color: K.mid }}>Chargement…</div>;
  if (d === null) return fallback;
  const fini = d.statut === "payee";
  return (
    <div style={{ background: K.cream, minHeight: "100vh", fontFamily: "'Montserrat', -apple-system, sans-serif", color: K.navy }} data-testid="paiement-page">
      <div style={{ background: K.navy, color: K.cream, padding: "18px 20px" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, letterSpacing: 2 }}>AU<span style={{ color: K.gold }}>PRÉA</span></div>
        <div style={{ fontSize: 13, opacity: .85 }}>Paiement · proposition n°{d.numero} · {d.client.prenom} {d.client.nom}</div>
      </div>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
        {retour === "verif" && <div style={card} data-testid="pay-retour-verif">Vérification de votre paiement…</div>}
        {retour === "ok" && <div style={{ ...card, borderColor: K.green, color: K.green, fontWeight: 700 }} data-testid="pay-retour-ok">✓ Paiement reçu, merci ! Votre facture acquittée vous a été envoyée par e-mail.</div>}
        {retour === "attente" && <div style={card} data-testid="pay-retour-attente">Votre paiement est en cours de confirmation. Cette page se mettra à jour dans quelques instants.</div>}
        {d.retractation ? (
          <div style={{ ...card, borderColor: K.red }} data-testid="pay-retracte">Rétractation enregistrée le {fdate(d.retractation.at)}. {d.remboursement ? `Remboursement de ${eur(d.remboursement.montant)} effectué.` : "Les sommes versées vous seront remboursées sous 14 jours."}</div>
        ) : fini ? (
          <div style={{ ...card, borderColor: K.green }} data-testid="pay-solde"><strong style={{ color: K.green }}>✓ Votre proposition est entièrement réglée.</strong> Merci pour votre confiance.</div>
        ) : <Regler token={token} d={d} reload={load} />}
        <Echeancier d={d} />
        {d.carnet && <div style={card} data-testid="pay-carnet">Votre {d.carnet.formule} est inclus jusqu'au <strong>{fdate(d.carnet.fin_at)}</strong>. Code d'activation : <strong style={{ fontFamily: "monospace" }} data-testid="pay-carnet-code">{d.carnet.code}</strong></div>}
        <Factures d={d} />
        <div style={{ ...card, borderLeft: `4px solid ${K.red}` }}><strong>Aucun règlement en espèces ne peut être remis à votre consultant.</strong></div>
        <a href={`/p/${token}`} style={{ display: "block", textAlign: "center", fontSize: 13, color: K.mid, margin: "10px 0 30px" }} data-testid="pay-retour-dossier">← Revenir à mes documents signés</a>
      </div>
    </div>
  );
}
